import LayoutFolderService from '../services/api/layout-folder'
import {
  ADDITIONAL_MULTI_LINE_TEXT_FIELD_PREFIX_ID,
  TOC_LINE_PREFIX_ID,
} from "@frontend/constants/templates-field-prefixes";

// Vuex state is deep-observed. preview.data holds the html_tables arrays (multi-MB JSON
// strings per table) and preview.template the field definitions - neither drives reactive
// rendering, but Vue was wrapping them in Observers (heap snapshots: ~99MB under
// "value in Observer"). `_isVue` is Vue's own opt-out marker for observe(). POR-13578.
function markPreviewNonReactive(preview) {
  if (!preview || typeof preview !== 'object') {
    return;
  }

  ['data', 'template'].forEach((key) => {
    const value = preview[key];

    if (value && typeof value === 'object' && !value._isVue) {
      Object.defineProperty(value, '_isVue', { value: true, enumerable: false, configurable: true });
    }
  });
}

export default {
  namespaced: true,

  state: () => ({
    selectedInstance: null,
    currentPagePreviews: [],
    folders: [],
    keepAspectRatio: false,
    target: null,
    layers: [],
    allProductImages: [],
    folder: null,
    allObjectsAreSelected: false,
    templatesCount: 0,
    isTemplatesResizing: false
  }),

  getters: {
    selectedInstance: state => state.selectedInstance,
    selectedInstanceIndex: state => _.findIndex(state.currentPagePreviews, ['id', state.selectedInstance.id]),
    selectedInstanceFieldsWithType: state => type => _.filter(state.selectedInstance.template.fields, ['type', type]),
    selectedInstanceAdditionalFields: state => {
      const allTextElements = state.selectedInstance.canvas.getObjects('textbox');

      const result = [];
      
      for (const element of allTextElements) {
        if (
          element.element_id.startsWith('add_multi_line_text')
          || element.element_id.startsWith('add_text')
          || element.element_id.startsWith(TOC_LINE_PREFIX_ID)
        ) {
          result.push({
            element_id: element.element_id,
            name: element.name,
            text: element.text,
            type: (
              element.element_id.startsWith('add_multi_line_text')
              || element.element_id.startsWith(TOC_LINE_PREFIX_ID)
            )
              ? 'Multi Line Text'
              : 'Text',
          });
        }
      }
      
      return result.sort((a, b) => {
        const aNameReversed = a.name.split('').reverse().join('');
        const bNameReversed = b.name.split('').reverse().join('');
        return aNameReversed.localeCompare(bNameReversed);
      });
    },
    selectedInstanceTextBoxFields: state => {
      return state.selectedInstance.canvas.getObjects('textbox').map((textField) => {
        return {
          element_id: textField.element_id,
          name: textField.name || textField?.templateField?.name,
          text: textField.text,
          type: (
            textField?.templateField?.type
            || (
              (
                textField.id.startsWith(ADDITIONAL_MULTI_LINE_TEXT_FIELD_PREFIX_ID)
                || textField.id.startsWith(TOC_LINE_PREFIX_ID)
              )
                ? 'Multi Line Text'
                : 'Text'
            )
          ),
          options: state.selectedInstance.template.fields.find(field => field.id == textField.fieldId)?.options,
          visible: textField.visible
        };
      }).reverse();
    },
    currentPagePreviews: state => state.currentPagePreviews,
    folders: state => state.folders,
    keepAspectRatio: state => state.keepAspectRatio,
    target: state => state.target,
    layers: state => state.layers,
    allProductImages: state => state.allProductImages,
    folder: state => state.folder,
    data: state => state.data,
    allObjectsAreSelected: state => state.allObjectsAreSelected,
    getInstanceById: (state) => (id) => {
      return state.currentPagePreviews.find(i => i.id === id);
    },
    allTemplatesCount: state => state.templatesCount,
    isTemplatesResizing: state => state.isTemplatesResizing
  },

  mutations: {
    SET_SELECTED_INSTANCE (state, payload) {
      markPreviewNonReactive(payload)
      state.selectedInstance = payload
    },
    SET_CURRENT_PAGE_PREVIEWS (state, payload) {
      (payload || []).forEach(markPreviewNonReactive)
      state.currentPagePreviews = payload
    },
    SET_FOLDERS (state, payload) {
      state.folders = payload
    },
    SET_KEEP_ASPECT_RATIO (state, payload) {
      state.keepAspectRatio = payload
    },
    SET_TARGET (state, payload) {
      state.target = payload
    },
    SET_LAYERS (state, payload) {
      state.layers = payload
    },
    SET_ALL_PRODUCT_IMAGES_TO_LAYERS (state, payload) {
      state.allProductImages = payload
    },
    ADD_FOLDER (state, payload) {
      state.folders.push(payload)
    },
    SET_FOLDER (state, payload) {
      state.folder = payload;
    },
    DELETE_LAYER (state, element_id) {
      const index = state.layers.findIndex(item => item.element_id === element_id)
      if (index) {
        state.layers.splice(index, 1)
      }
    },
    SET_ALL_OBJECTS_SELECTED (state, payload) {
      state.allObjectsAreSelected = payload
    },
    SET_TEMPLATES_COUNT (state, payload) {
      state.templatesCount = payload
    },
    SET_IS_TEMPLATES_RESIZING (state, payload) {
      state.isTemplatesResizing = payload;
    }
  },

  actions: {
    async fetchFolders ({ commit }, params = {}) {
      const { data } = await LayoutFolderService.getFolders(params)
      commit('SET_FOLDERS', data.items)
    },
    async createFolder ({ commit }, payload) {
      const { data } = await LayoutFolderService.createFolder(payload.parent_id, payload.name)
      commit('ADD_FOLDER', data)
      return data
    },
    async checkFolderName ({ commit }, payload){
      const { data } = await LayoutFolderService.checkFolderName(payload)
      return data
    },
    async getFolder ({ commit }, payload) {
      const { data } = await LayoutFolderService.getFolder(payload);
      commit('SET_FOLDER', data);
    }
  }
}
